(function () {
  'use strict';

  var openerByPanel = new WeakMap();
  var modalBackgrounds = new WeakMap();
  var openModals = new Set();
  var observedCartCounts = new WeakSet();
  var tabListIndex = 0;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function directChild(element, selector) {
    return Array.prototype.find.call(element.children, function (child) {
      return child.matches(selector);
    });
  }

  function controlledPanel(trigger) {
    var panelId = trigger.getAttribute('aria-controls') || trigger.getAttribute('data-drawer-id');
    return panelId ? document.getElementById(panelId) : null;
  }

  function rememberOpener(trigger) {
    var panel = controlledPanel(trigger);
    if (panel) openerByPanel.set(panel, trigger);
  }

  function updateModalBackground(dialog, isOpen) {
    var existing = modalBackgrounds.get(dialog);

    if (!isOpen) {
      openModals.delete(dialog);
      if (!existing) return;
      existing.forEach(function (entry) {
        entry.element.inert = entry.wasInert;
      });
      modalBackgrounds.delete(dialog);
      return;
    }

    if (existing) return;

    var background = [];
    var branch = dialog;
    while (branch.parentElement) {
      var parent = branch.parentElement;
      Array.prototype.forEach.call(parent.children, function (sibling) {
        if (sibling === branch || sibling.matches('script, style, link, .PageOverlay')) return;
        background.push({ element: sibling, wasInert: sibling.inert });
        sibling.inert = true;
      });
      if (parent === document.body) break;
      branch = parent;
    }

    modalBackgrounds.set(dialog, background);
    openModals.add(dialog);
  }

  function updatePanelState(panel) {
    if (panel.matches('.PageOverlay, .LoadingBar')) return;
    // aria-hidden on a link means hide from at, not disable: inert would take the click too
    if (panel.matches('a[href]')) return;
    // newer flickity hides on-screen cells too
    if (panel.parentElement && panel.parentElement.classList.contains('flickity-slider')) return;
    // apps hide the page from at, not from clicks
    var main = document.getElementById('main');
    if (main && panel.contains(main)) return;

    var isHidden = panel.hidden || panel.getAttribute('aria-hidden') === 'true';
    var containedFocus = panel.contains(document.activeElement);

    if ('inert' in panel) panel.inert = isHidden;

    if (panel.matches('[role="dialog"][aria-modal="true"]')) {
      updateModalBackground(panel, !isHidden);
    } else if (modalBackgrounds.has(panel)) {
      updateModalBackground(panel, false);
    }

    if (panel.id) {
      document.querySelectorAll('[aria-controls="' + CSS.escape(panel.id) + '"], [data-action="open-drawer"][data-drawer-id="' + CSS.escape(panel.id) + '"]').forEach(function (trigger) {
        if (trigger.getAttribute('role') === 'tab') return;
        if (trigger.hasAttribute('aria-expanded') || trigger.matches('[data-action="open-drawer"]')) {
          trigger.setAttribute('aria-expanded', String(!isHidden));
        }
      });
    }

    if (isHidden && containedFocus) {
      var opener = openerByPanel.get(panel);
      if (opener && document.contains(opener)) {
        window.requestAnimationFrame(function () {
          // scroll-padding treats the sticky header as obscured
          opener.focus({ preventScroll: true });
        });
      }
    }
  }

  function prepareDisclosureMenus(root) {
    root.querySelectorAll('.Header__MainNav [aria-haspopup]').forEach(function (item, index) {
      var trigger = directChild(item, 'a[href], button');
      var panel = directChild(item, '.MegaMenu, .DropdownMenu');
      if (!trigger || !panel) return;

      if (!panel.id) panel.id = 'navigation-panel-' + index;
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', String(panel.getAttribute('aria-hidden') !== 'true'));
      trigger.setAttribute('data-a11y-disclosure-opener', '');
      updatePanelState(panel);
    });
  }

  function activateTab(tab, shouldFocus) {
    var tabList = tab.closest('[role="tablist"]');
    if (!tabList) return;
    // hiding slick's panels strands the current slide
    if (tabList.closest('.slick-slider')) return;

    tabList.querySelectorAll('[role="tab"]').forEach(function (candidate) {
      var isSelected = candidate === tab;
      candidate.setAttribute('aria-selected', String(isSelected));
      candidate.tabIndex = isSelected ? 0 : -1;
      candidate.classList.toggle('is-active', isSelected);

      var panelId = candidate.getAttribute('aria-controls');
      var panel = panelId && document.getElementById(panelId);
      if (panel) {
        panel.hidden = !isSelected;
        panel.setAttribute('aria-hidden', String(!isSelected));
      }
    });

    if (shouldFocus) tab.focus();
  }

  function prepareTabs(root) {
    root.querySelectorAll('[role="tablist"]').forEach(function (tabList) {
      if (!tabList.id) {
        tabListIndex += 1;
        tabList.id = 'a11y-tablist-' + tabListIndex;
      }
      if (!tabList.hasAttribute('aria-label') && !tabList.hasAttribute('aria-labelledby')) {
        var sectionHeading = tabList.closest('section') && tabList.closest('section').querySelector('.SectionHeader__SubHeading, .SectionHeader__Heading');
        if (sectionHeading) {
          if (!sectionHeading.id) sectionHeading.id = tabList.id + '-label';
          tabList.setAttribute('aria-labelledby', sectionHeading.id);
        }
      }

      var tabs = tabList.querySelectorAll('[role="tab"]');
      var selectedTab = Array.prototype.find.call(tabs, function (tab) {
        return tab.getAttribute('aria-selected') === 'true' || tab.classList.contains('is-active');
      }) || tabs[0];

      tabs.forEach(function (tab, index) {
        var panelId = tab.getAttribute('aria-controls');
        var panel = panelId && document.getElementById(panelId);
        if (!tab.id) tab.id = tabList.id + '-tab-' + index;
        if (panel) panel.setAttribute('aria-labelledby', tab.id);
      });

      if (selectedTab) activateTab(selectedTab, false);
    });
  }

  function pauseCarousel(section, shouldPause) {
    var carousel = section.querySelector('.Slideshow__Carousel');
    var instance = window.Flickity && carousel ? window.Flickity.data(carousel) : null;
    if (!instance || !instance.options.autoPlay) return;

    if (shouldPause) instance.pausePlayer();
    else instance.unpausePlayer();
  }

  function setCarouselButton(button, isPaused) {
    var pauseLabel = button.getAttribute('data-pause-label') || 'Pause slideshow';
    var playLabel = button.getAttribute('data-play-label') || 'Play slideshow';
    // textContent would wipe the glyph
    button.setAttribute('aria-label', isPaused ? playLabel : pauseLabel);
    button.setAttribute('data-paused', String(isPaused));
  }

  function setVideoButton(button, video) {
    var isPaused = video.paused;
    var pauseLabel = button.getAttribute('data-pause-label') || 'Pause video';
    var playLabel = button.getAttribute('data-play-label') || 'Play video';
    button.textContent = isPaused ? playLabel : pauseLabel;
  }

  function honorMotionPreference(root) {
    if (!reducedMotion.matches) return;

    root.querySelectorAll('[data-section-type="slideshow"]').forEach(function (section) {
      section.setAttribute('data-a11y-motion-paused', 'true');
      pauseCarousel(section, true);
      var control = section.querySelector('[data-a11y-carousel-toggle]');
      if (control) setCarouselButton(control, true);
    });

    root.querySelectorAll('video[autoplay]').forEach(function (video) {
      video.pause();
      var container = video.closest('.Slideshow__ImageContainer');
      var control = container && container.querySelector('[data-a11y-video-toggle]');
      if (control) setVideoButton(control, video);
    });
  }

  function prepareCartStatus(root) {
    root.querySelectorAll('[data-header-cart-count]').forEach(function (count) {
      if (observedCartCounts.has(count)) return;
      observedCartCounts.add(count);

      var status = document.querySelector('[data-header-cart-status]');
      if (!status) return;

      new MutationObserver(function () {
        var label = status.getAttribute('data-label') || 'Cart';
        status.textContent = label + ': ' + (count.textContent.trim() || '0');
      }).observe(count, { childList: true, characterData: true, subtree: true });
    });
  }

  function initialize(root) {
    prepareDisclosureMenus(root);
    prepareTabs(root);
    prepareCartStatus(root);

    root.querySelectorAll('[aria-hidden]').forEach(updatePanelState);

    // server error renders inside the closed dialog
    var priceMatchError = root.querySelector('#price-match-dialog .Form__Alert.Alert--error[tabindex="-1"]');
    if (priceMatchError) {
      var priceMatchDialog = priceMatchError.closest('#price-match-dialog');
      var priceMatchTrigger = document.getElementById('price-match');
      document.body.classList.add('active-call-price');
      priceMatchDialog.setAttribute('aria-hidden', 'false');
      updatePanelState(priceMatchDialog);
      if (priceMatchTrigger) {
        priceMatchTrigger.setAttribute('aria-expanded', 'true');
        openerByPanel.set(priceMatchDialog, priceMatchTrigger);
      }
    }

    var summaries = root.querySelectorAll('.Form__Alert.Alert--error[tabindex="-1"]');
    var visibleSummary = Array.prototype.find.call(summaries, function (summary) {
      return summary.getClientRects().length > 0;
    });
    if (visibleSummary) visibleSummary.focus();

    honorMotionPreference(root);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-action="open-drawer"], [data-action="open-modal"], .search__uniq, [data-a11y-disclosure-opener], [aria-controls][aria-expanded]:not([role="tab"])');
    if (trigger) rememberOpener(trigger);

    var priceMatchTrigger = event.target.closest('#price-match');
    if (priceMatchTrigger) {
      var priceMatchDialog = document.getElementById('price-match-dialog');
      if (priceMatchDialog) {
        document.body.classList.add('active-call-price');
        priceMatchDialog.setAttribute('aria-hidden', 'false');
        priceMatchTrigger.setAttribute('aria-expanded', 'true');
        window.requestAnimationFrame(function () {
          var initialFocus = priceMatchDialog.querySelector('#price-match-dialog-title, #callClose');
          if (initialFocus) initialFocus.focus();
        });
      }
    }

    if (event.target.closest('#callClose')) {
      var openPriceMatchDialog = document.getElementById('price-match-dialog');
      document.body.classList.remove('active-call-price');
      if (openPriceMatchDialog) openPriceMatchDialog.setAttribute('aria-hidden', 'true');
    }

    var selectedTab = event.target.closest('[role="tab"]');
    if (selectedTab) activateTab(selectedTab, false);

    var carouselButton = event.target.closest('[data-a11y-carousel-toggle]');
    if (carouselButton) {
      var section = carouselButton.closest('[data-section-type="slideshow"]');
      var isPaused = section.getAttribute('data-a11y-motion-paused') !== 'true';
      section.setAttribute('data-a11y-motion-paused', String(isPaused));
      pauseCarousel(section, isPaused);
      setCarouselButton(carouselButton, isPaused);
    }

    var videoButton = event.target.closest('[data-a11y-video-toggle]');
    if (videoButton) {
      var videoContainer = videoButton.closest('.Slideshow__ImageContainer');
      var video = videoContainer && videoContainer.querySelector('video');
      if (!video) return;
      if (video.paused) video.play();
      else video.pause();
      setVideoButton(videoButton, video);
    }
  });

  document.addEventListener('focusin', function (event) {
    var trigger = event.target.closest('[data-action="open-drawer"], [data-action="open-modal"], .search__uniq, [data-a11y-disclosure-opener], [aria-controls][aria-expanded]:not([role="tab"])');
    if (trigger) rememberOpener(trigger);

    var section = event.target.closest('[data-section-type="slideshow"]');
    if (section) pauseCarousel(section, true);
  });

  document.addEventListener('focusout', function (event) {
    var section = event.target.closest('[data-section-type="slideshow"]');
    if (!section) return;

    window.requestAnimationFrame(function () {
      if (!section.contains(document.activeElement) &&
          section.getAttribute('data-a11y-motion-paused') !== 'true' &&
          !reducedMotion.matches) {
        pauseCarousel(section, false);
      }
    });
  });

  document.addEventListener('keydown', function (event) {
    var currentTab = event.target.closest('[role="tab"]');
    if (currentTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(event.key) !== -1) {
      var tabList = currentTab.closest('[role="tablist"]');
      var tabs = Array.prototype.slice.call(tabList.querySelectorAll('[role="tab"]'));
      var currentIndex = tabs.indexOf(currentTab);
      var nextIndex = currentIndex;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      event.preventDefault();
      tabs[nextIndex].click();
      activateTab(tabs[nextIndex], true);
      return;
    }

    var activeDialog = event.target.closest('[role="dialog"][aria-modal="true"][aria-hidden="false"]');
    if (activeDialog && event.key === 'Tab' && !activeDialog.closest('modal-dialog') && activeDialog.id !== 'atc-popup-overlay') {
      var focusable = Array.prototype.filter.call(
        activeDialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (element) { return element.getClientRects().length > 0; }
      );
      if (!focusable.length) {
        event.preventDefault();
        activeDialog.focus();
      } else if (event.shiftKey && document.activeElement === focusable[0]) {
        event.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (!event.shiftKey && document.activeElement === focusable[focusable.length - 1]) {
        event.preventDefault();
        focusable[0].focus();
      }
      return;
    }

    if (event.key === 'Escape' && activeDialog && (activeDialog.id === 'price-match-dialog' || activeDialog.id === 'call-for-price-dialog')) {
      document.body.classList.remove('active-call-price');
      activeDialog.setAttribute('aria-hidden', 'true');
      event.preventDefault();
      return;
    }

    if (event.key !== 'Escape') return;

    var panel = event.target.closest('.MegaMenu[aria-hidden="false"], .DropdownMenu[aria-hidden="false"]');
    if (!panel) return;

    var item = panel.parentElement && panel.parentElement.closest('.Header__MainNav li');
    panel.setAttribute('aria-hidden', 'true');
    if (item) item.classList.remove('is-expanded');
    var trigger = item && directChild(item, 'a[href], button');
    if (trigger) trigger.focus();
    event.stopPropagation();
  });

  document.addEventListener('invalid', function (event) {
    event.target.setAttribute('aria-invalid', 'true');
  }, true);

  document.addEventListener('input', function (event) {
    if (event.target.matches('input, select, textarea') && event.target.validity.valid) {
      event.target.removeAttribute('aria-invalid');
    }
  });

  var panelObserver = new MutationObserver(function (records) {
    var removed = false;

    records.forEach(function (record) {
      if (record.type === 'attributes') {
        updatePanelState(record.target);
        return;
      }

      if (record.removedNodes.length) removed = true;

      Array.prototype.forEach.call(record.addedNodes, function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches('[aria-hidden]')) updatePanelState(node);
        node.querySelectorAll('[aria-hidden]').forEach(updatePanelState);
      });
    });

    // removed while open, it never flips aria-hidden
    if (removed) {
      openModals.forEach(function (dialog) {
        if (!dialog.isConnected) updateModalBackground(dialog, false);
      });
    }
  });

  // focus rings follow keyboard navigation
  function setInputModality(next) {
    if (document.documentElement.getAttribute('data-input-modality') === next) return;
    document.documentElement.setAttribute('data-input-modality', next);
  }

  var CARET_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];

  function isTextEntry(element) {
    if (!element) return false;
    if (element.isContentEditable) return true;
    if (element.tagName === 'TEXTAREA') return true;
    if (element.tagName !== 'INPUT') return false;
    return ['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].indexOf(element.type) === -1;
  }

  // typing is not navigating, only a focus move rings
  function movesFocus(event) {
    if (event.key === 'Tab' || event.key === 'Escape') return true;
    if (CARET_KEYS.indexOf(event.key) === -1) return false;
    return !isTextEntry(event.target);
  }

  setInputModality('pointer');
  document.addEventListener('keydown', function (event) {
    if (event.metaKey || event.altKey || event.ctrlKey) return;
    if (!movesFocus(event)) return;
    setInputModality('keyboard');
  }, true);
  document.addEventListener('pointerdown', function () {
    setInputModality('pointer');
  }, true);

  var callPriceDialogs = [
    { id: 'price-match-dialog', trigger: '#price-match', focus: ['#price-match-dialog-title', '#callClose'] },
    { id: 'call-for-price-dialog', trigger: '.pdp-call-btn', focus: ['#call-for-price-dialog-title', '#call-for-price-dialog-product', '#callClose'] }
  ];

  // the opener stops propagation, class is the only signal
  var priceMatchObserver = new MutationObserver(function () {
    var isOpen = document.body.classList.contains('active-call-price');

    callPriceDialogs.forEach(function (config) {
      var dialog = document.getElementById(config.id);
      if (!dialog) return;
      if ((dialog.getAttribute('aria-hidden') === 'false') === isOpen) return;

      var trigger = document.querySelector(config.trigger);
      if (trigger) openerByPanel.set(dialog, trigger);
      dialog.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

      window.requestAnimationFrame(function () {
        if (isOpen) {
          // a selector list would return dom order
          var initialFocus = null;
          for (var i = 0; i < config.focus.length && !initialFocus; i++) {
            initialFocus = dialog.querySelector(config.focus[i]);
          }
          if (initialFocus) initialFocus.focus();
        } else if (trigger && document.contains(trigger)) {
          trigger.focus();
        }
      });
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    initialize(document);
    priceMatchObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    panelObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'hidden', 'role', 'aria-modal'],
      childList: true,
      subtree: true
    });

    window.requestAnimationFrame(function () {
      honorMotionPreference(document);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });

  if (typeof reducedMotion.addEventListener === 'function') {
    reducedMotion.addEventListener('change', function () {
      honorMotionPreference(document);
    });
  }
})();
